// src/services/httpService.js

import axios from "axios";

const DEFAULT_TIMEOUT_MS = 15000;
const LONG_RUNNING_ENDPOINT_TIMEOUTS = {
  "/admin/newsroom/dry-run": 180000,
  "/admin/newsroom/catalog-search": 30000,
  "/admin/analytics/engagement-report": 180000,
};

const httpInstance = axios.create({
  //baseURL: "http://localhost:3007/api/v1",
  baseURL: process.env.NODE_ENV === "production" ? "https://lyricsweb.com/api/v1" : process.env.REACT_APP_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
});

/**
 * Updates the Axios instance configuration dynamically.
 *
 * @param {Object} newConfig - New configuration settings to be merged.
 */
export function updateHttpConfig(newConfig) {
  Object.assign(httpInstance.defaults, newConfig);
}

function getRequestConfig(endpoint, config = {}) {
  const timeout = config.timeout ?? LONG_RUNNING_ENDPOINT_TIMEOUTS[endpoint] ?? DEFAULT_TIMEOUT_MS;
  return { ...config, timeout };
}

function describeMissingResponse(error, endpoint) {
  const isTimeout =
    error?.code === "ECONNABORTED" ||
    error?.code === "ETIMEDOUT" ||
    String(error?.message || "").toLowerCase().includes("timeout");

  if (isTimeout) {
    return `No response from ${endpoint} before the request timeout. Verify the run status before retrying.`;
  }

  return `No response from ${endpoint}. Check the admin session or API availability and retry once.`;
}

/**
 * Makes an HTTP request using the Axios instance with dynamic method and optional additional configuration.
 *
 * @param {string} method - The HTTP method to use for the request (e.g., 'GET', 'POST', 'PUT', 'DELETE').
 * @param {string} endpoint - The endpoint to send the request to.
 * @param {Object} [data={}] - The data to be sent in the request body (for POST, PUT, PATCH).
 * @param {Object} [config={}] - Optional Axios configuration.
 * @returns {Promise} - Axios promise for the HTTP request.
 */
export async function makeRequest(method, endpoint, data = {}, config = {}) {
  try {
    // Extract headers from config, ensure they don't end up in params
    const { headers = {}, ...restConfig } = getRequestConfig(endpoint, config);

    // Handle different HTTP methods
    switch (method.toUpperCase()) {
      case "GET":
        // For GET requests: data goes to params, headers stay as headers
        return await httpInstance.get(endpoint, {
          params: data,
          headers,
          ...restConfig
        });
      case "POST":
        return await httpInstance.post(endpoint, data, { headers, ...restConfig });
      case "PUT":
        return await httpInstance.put(endpoint, data, { headers, ...restConfig });
      case "DELETE":
        return await httpInstance.delete(endpoint, { headers, ...restConfig, data });
      case "PATCH":
        return await httpInstance.patch(endpoint, data, { headers, ...restConfig });
      default:
        throw new Error(`Unsupported method: ${method}`);
    }
  } catch (error) {
    if (error?.request && !error?.response) {
      error.message = describeMissingResponse(error, endpoint);
    }
    console.error(`Error making ${method} request to ${endpoint}:`, error);
    throw error;
  }
}
